import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const resolveTag = (value, allTags) => allTags.find(t => t.value === value);

const TagCellEditor = ({ params, allTags, onToggleTag, onManageTags }) => {
  const [open, setOpen] = useState(false);
  // Mirrors inFlightRef for rendering -- which tag values currently have a save in flight for this
  // row. Per-tag rather than one row-wide flag: a save on tag A must not block a click on tag B, or
  // the lost-update race this component used to own becomes unreachable through the UI (see
  // tags/README.md). The build, the generation gate and the correction-on-delete for the race itself
  // now live in Pipeline.jsx's handleTagToggle, alongside `properties` state and the row's other
  // save disciplines -- this component only owns the button-disabled UI around that call.
  const [inFlightTags, setInFlightTags] = useState(() => new Set());
  const [dropdownStyle, setDropdownStyle] = useState({});
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const inFlightRef = useRef(new Set());

  const assigned = params.row.tagsAssigned ?? [];

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleOpen = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
        zIndex: 9999,
        width: 260,
      });
    }
    setOpen(o => !o);
  };

  const toggle = async (value) => {
    // Defense in depth alongside the per-tag `disabled` below: a disabled real <button> does not
    // dispatch click at all, but this also protects any caller that isn't gated through the DOM.
    if (inFlightRef.current.has(value)) return;

    inFlightRef.current.add(value);
    setInFlightTags(new Set(inFlightRef.current));
    try {
      // onToggleTag (Pipeline.jsx's handleTagToggle) always resolves, even on a failed PATCH -- it
      // owns markSaveOk/markSaveFailed itself, so there is nothing for this component to branch on.
      await onToggleTag(params.row.id, value, assigned);
    } finally {
      inFlightRef.current.delete(value);
      setInFlightTags(new Set(inFlightRef.current));
    }
  };

  const handleManageTags = () => {
    setOpen(false);
    onManageTags();
  };

  return (
    <div className="flex items-center gap-x-[4px] w-full h-full px-[4px] overflow-hidden">
      <button
        ref={buttonRef}
        onClick={handleOpen}
        disabled={inFlightTags.size > 0}
        style={{ border: '1.5px solid #DAE4D5' }}
        className=" flex-shrink-0 w-[22px] h-[22px] rounded-full bg-transparent text-brand-unselectedGray text-[15px] leading-none flex items-center justify-center hover:border-brand-green hover:text-brand-green transition-colors"
      >
        +
      </button>
      
      {assigned.map(v => {
        const tag = resolveTag(v, allTags);
        return (
          <span
            key={v}
            style={{ backgroundColor: tag?.color ?? '#DAE4D5' }}
            className="inline-flex items-center px-[8px] h-[22px] rounded-full text-[10px] font-bold text-white uppercase whitespace-nowrap flex-shrink-0"
          >
            {tag?.label ?? v}
          </span>
        );
      })}

      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{ ...dropdownStyle, background: '#1B2D1B', borderRadius: 14, maxHeight: 320, overflowY: 'auto' }}
          className="shadow-xl flex flex-col"
        >
          {allTags.length === 0 && (
            <p className="px-[16px] py-[12px] text-[13px] text-white/60">No tags yet</p>
          )}

          {allTags.map(tag => {
            const isSelected = assigned.includes(tag.value);
            return (
              <button
                key={tag.value}
                onClick={() => toggle(tag.value)}
                disabled={inFlightTags.has(tag.value)}
                style={{ background: isSelected ? '#243824' : 'transparent' }}
                className="flex items-center gap-x-[12px] px-[16px] w-full text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#243824'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
              >
                <span
                  style={{ backgroundColor: tag.color, minWidth: 20, height: 20, borderRadius: '50%', display: 'inline-block' }}
                />
                <span className="text-white text-[14px] py-[12px]">{tag.label}</span>
              </button>
            );
          })}

          <div style={{ borderTop: '1px solid #2A3D2A', padding: '10px 12px' }}>
            <button
              onClick={handleManageTags}
              style={{ border: '1.5px solid #4CBC18', borderRadius: 999, color: '#4CBC18', width: '100%', height: 44, background: 'transparent', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              Manage Tags
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TagCellEditor;
